// Kitchen-view stack grouping. Collapses identical pantry rows
// (same canonical + brand + package size + claims + location +
// tile) into a single visual card with a count, instead of showing
// N stacked-up duplicates.
//
// The DATA MODEL stays 1-row-per-physical-package — every can of
// Hunt's Diced Tomatoes is its own pantry_items row because each
// has its own expiry, partial-remaining amount, and shelf. This
// module ONLY changes how the kitchen view renders them. Each
// "group" carries a `leader` (the representative card the grid
// renders) plus `members` (the full row list for drill-in).
//
// Grouping key matches what the user perceives as "the same
// product I bought multiple of":
//
//   canonical_id     — same product identity
//   brand            — case-insensitive (Hunt's vs HUNT'S merge)
//   package_amount   — same package size as sold
//   package_unit     — same unit
//   claims (sorted)  — same variant (BBQ vs Classic split)
//   _location        — same physical place (fridge vs pantry split)
//   _tileId          — same shelf within that location
//
// Anything missing on the canonical-id axis falls through to its
// own singleton group — without identity we can't confidently
// merge. (Manually-added free-text rows stay distinct.)
//
// Sibling to src/lib/pantryFormat.js's `identityKey` / `groupByIdentity`,
// which uses a narrower key (no brand / no package size). Kept
// separate because pantryFormat's stack semantics drive the OLD
// kitchen stack logic (used in Kitchen.jsx); this module is the
// CURRENT kitchen view's grouping primitive.

// Compose the grouping key for a card (toCard-shaped object from
// src/lib/pantryHelpers.js). Reads identity from card._raw when
// present (the original pantry_items row) and falls back to
// top-level card fields for demo / search-shape rows.
export function kitchenIdentityKey(card) {
  if (!card) return "__null";
  const raw = card._raw || card;
  const canonical = String(raw.canonicalId || card.canonicalId || "")
    .toLowerCase()
    .trim();

  // Without a bound canonical we can't safely merge — return a
  // unique key per row so each unbound item stays its own group.
  // Random suffix on null id is defensive for free-text rows that
  // skipped through with no identifier.
  if (!canonical) {
    return `__singleton:${card.id || raw.id || Math.random().toString(36).slice(2)}`;
  }

  // Case-insensitive brand merge per the user's explicit pref —
  // "Hunt's" and "HUNT'S" collapse to the same group. Null brand
  // stays null (not coerced to ""); a row with brand=null and a
  // row with brand="Hunt's" are different groups.
  const brand = raw.brand != null
    ? String(raw.brand).toLowerCase().trim()
    : "_NULL_";

  const pAmt = raw.packageAmount != null && Number.isFinite(Number(raw.packageAmount))
    ? Number(raw.packageAmount)
    : "_NULL_";
  const pUnit = raw.packageUnit != null
    ? String(raw.packageUnit).toLowerCase().trim()
    : "_NULL_";

  // Claims define variants — Lay's BBQ vs Lay's Classic split.
  // Sort so ["BBQ","Limited"] and ["Limited","BBQ"] hash identically.
  // Case-insensitive on the tokens too.
  const claimSrc = Array.isArray(raw.claims) ? raw.claims : (Array.isArray(card.claims) ? card.claims : []);
  const claims = claimSrc
    .map((c) => String(c || "").toLowerCase().trim())
    .filter(Boolean)
    .sort()
    .join(",");

  // Location + tile keep physically-separated stacks distinct.
  // One can in the fridge and one in the pantry SHOULD NOT collapse
  // — they're in different places.
  const location = String(card._location || raw.location || "").toLowerCase();
  const tile = String(card._tileId || raw.tileId || raw.tile_id || "").toLowerCase();

  return [canonical, brand, pAmt, pUnit, claims, location, tile].join("|");
}

// Walk a cards list and emit identity-grouped buckets, preserving
// the input order (the first card in each bucket wins the slot in
// the rendered grid).
//
// Returns: Array<{ key, leader, members }>.
//   key      — the kitchenIdentityKey string
//   leader   — the first card seen for this key (rendered in the grid)
//   members  — every card sharing this key (length 1 for singletons)
export function groupCardsForKitchen(cards) {
  if (!Array.isArray(cards) || cards.length === 0) return [];
  const buckets = new Map();
  const out = [];
  for (const card of cards) {
    if (!card) continue;
    const key = kitchenIdentityKey(card);
    const existing = buckets.get(key);
    if (existing) {
      existing.members.push(card);
    } else {
      const bucket = { key, leader: card, members: [card] };
      buckets.set(key, bucket);
      out.push(bucket);
    }
  }
  return out;
}

// Quick summary of per-instance state across a group's members.
// Used by the group card subtitle ("1 partial · 2 full") and by the
// StackDetailSheet header.
//
// "partial" = a row whose `amount` is less than its `max` (the user
// has started using one of the packages). "full" = amount === max.
// Discrete-count units where amount=1 always read as "full" (one
// sealed package).
export function summarizeStack(members) {
  if (!Array.isArray(members) || members.length === 0) {
    return { count: 0, full: 0, partial: 0, earliestExpiry: null };
  }
  let full = 0;
  let partial = 0;
  let earliestExpiry = null;
  for (const m of members) {
    const amount = Number(m.amount);
    const max = Number(m.max);
    if (Number.isFinite(amount) && Number.isFinite(max) && max > 0 && amount < max) {
      partial += 1;
    } else {
      full += 1;
    }
    const exp = m._raw?.expiresAt || m.expiresAt || null;
    if (exp && (!earliestExpiry || new Date(exp) < new Date(earliestExpiry))) {
      earliestExpiry = exp;
    }
  }
  return { count: members.length, full, partial, earliestExpiry };
}
